import {
  ApacCandidateFooter,
  ApacCandidateHeader,
} from '@seek/apac-candidate-header-footer';
import { useAppContext, useConfig } from '@seek/libs-shared';
import type { Language } from '@seek/melways-sites';
import { Box, Page, PageBlock, Stack, Text } from 'braid-design-system';
import { useParams } from 'react-router';

import type { AppConfig } from '../../../shared/config';
import { useAuth } from '../../../shared/providers/authProvider';
import { getLanguageSwitcher, getLanguageUrl } from './languageSwitcher';

import { useHubble } from '@seek/hubble';
import { useFeatureFlags } from '../../../shared/providers/featureFlagProvider';
import * as styles from './MainLayout.css';

interface Props extends React.PropsWithChildren {
  pageWidth?: 'large' | 'medium' | undefined;
  showHeaderDivider?: boolean;
  showMarginTopAndBottom?: boolean;
}

export function MainLayout({
  children,
  pageWidth = 'large',
  showHeaderDivider = false,
  showMarginTopAndBottom = true,
}: Props) {
  const config = useConfig<AppConfig>();
  const urlParams = useParams<'locationSlug'>();

  const { userId } = useAppContext();
  const hubble = useHubble();
  hubble.updateOptions({ loginId: userId });

  const { authenticationStatus, userProfile } = useAuth();
  const { cookieFeatureFlags } = useFeatureFlags();

  if (typeof window !== 'undefined') {
    config.absoluteUrl = location.href;
  }

  const languageSwitcher = getLanguageSwitcher({ urlParams, config });

  const handleLanguageSwitch = (selectedLanguage: Language) =>
    location.replace(
      getLanguageUrl({
        config,
        urlParams,
        selectedLanguage,
        absoluteUrl: location.href,
      }),
    );

  return (
    <Page
      footer={
        <Box className={styles.footerWrapper}>
          {Object.values(cookieFeatureFlags).some((f) => f === true) && (
            <Box padding="large">
              <Text>
                Feature Flags enabled:{' '}
                {Object.entries(cookieFeatureFlags)
                  .filter((f) => f[1] === true)
                  .map((f) => f[0])
                  .join(', ')}
              </Text>
            </Box>
          )}
          <ApacCandidateFooter
            authenticationStatus={authenticationStatus}
            isExpandedVersion
          />
        </Box>
      }
      footerPosition="belowFold"
    >
      <Stack space={showMarginTopAndBottom ? 'large' : 'none'}>
        <ApacCandidateHeader
          authenticationStatus={authenticationStatus}
          userProfile={userProfile}
          returnUrlForPostAuth="/companies"
          activePrimaryTab="company"
          languageSwitcher={languageSwitcher}
          divider={showHeaderDivider}
          onLanguageSwitch={handleLanguageSwitch}
        />
        <PageBlock width={pageWidth}>
          <Box className={styles.body}>{children}</Box>
        </PageBlock>
      </Stack>
    </Page>
  );
}
